import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer'
import { generateMeasurementLabel } from 'features/kestrelViewer/hooks/useAnnotationCounters'
import { recordMeasurementCreated } from 'features/kestrelViewer/metrics'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { KVMeasurement, KVMeasurementJSON, RolodexEntryPatchJson } from 'types'

import { KVMessageDef } from './index'

export function getNewMeasurementMessage() {
  return {
    type: 'newMeasurement',
    requiredData: ['measurementId'],

    callback: (
      json: KVMeasurementJSON,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      const measurement = KVMeasurement.fromJSON(json)

      // Ignore if measurement has no segments
      if (!measurement.segments || measurement.segments.length === 0) {
        return
      }

      // Determine if this measurement already exists
      const existingIndex = kv.measurements?.findIndex(
        (m) => m.id === measurement.id
      )

      // If updating an existing measurement, preserve previously assigned label and user info
      if (existingIndex !== undefined && existingIndex !== -1) {
        const existing = kv.measurements![existingIndex]
        if (!measurement.label) {
          measurement.label = existing.label
        }
        measurement.tags = existing.tags
        // Preserve original creator info
        measurement.userName = existing.userName
        measurement.userEmail = existing.userEmail
        measurement.createdAt = existing.createdAt
      } else {
        // First time seeing this measurement: assign default if missing
        measurement.label = generateMeasurementLabel(
          measurement.label,
          kv.nextMeasurementNumber
        )

        // Set user metadata for new measurements
        measurement.userName = kv.user?.name ?? ''
        measurement.userEmail = kv.user?.email ?? ''
        // createdAt set in constructor

        // Apply whiteboard tag immediately if in SystemView context (for new measurements only)
        if (kv.selectedWhiteboardId) {
          measurement.tags = {
            ...measurement.tags,
            whiteboard: {
              derivedTag: false,
              value: kv.selectedWhiteboardId,
            },
          }
        }
      }

      if (kv.measurements) {
        let newKVMeasurements = [...kv.measurements]
        const isNewMeasurement =
          existingIndex === undefined || existingIndex === -1

        // If there is already a measurement with the same id, update the measurement
        if (!isNewMeasurement) {
          newKVMeasurements[existingIndex] = measurement
        } else {
          newKVMeasurements = [...kv.measurements, measurement]
        }
        kv.setMeasurements(newKVMeasurements)

        // Increment the measurement counter for new measurements only
        if (isNewMeasurement) {
          kv.setNextMeasurementNumber(kv.nextMeasurementNumber + 1)

          // Track measurement creation for session metrics
          recordMeasurementCreated()

          // Notify pixel stream about the label for new measurements
          if (kv.pixelStream) {
            kv.pixelStream.emitUIInteraction({
              command: KestrelCommands.UPDATE_MEASUREMENT,
              measurementId: measurement.id,
              measurementLabel: measurement.label,
            })
          }
        }

        const patchData: RolodexEntryPatchJson = {
          id: kv.kestrelModel?.id ?? '',
          data: {
            measurements:
              newKVMeasurements?.map((m) => KVMeasurement.toJSON(m)) ?? [],
          },
        }
        kv.rolodexPatch([patchData])
      }
    },
  } as KVMessageDef
}
